import type { Client } from "~/lib/matrix";
import { sendScheduledText } from "~/lib/matrix";

const DB_NAME = "chat-scheduled";
const STORE_NAME = "messages";
const DB_VERSION = 3;
const CHANNEL_NAME = "chat-scheduled";
const MAX_TIMEOUT_MS = 2_147_483_647;
const DISPATCH_LOCK_MS = 120_000;
const MAX_ATTEMPTS = 3;

export type ScheduledMessageStatus = "pending" | "dispatching" | "failed";

export interface ScheduledMessageRow {
  id: string;
  roomId: string;
  sendAt: number;
  body: string;
  formattedBody?: string;
  userId: string;
  txnId: string;
  createdAt: number;
  status: ScheduledMessageStatus;
  attempts: number;
  nextAttemptAt?: number;
  lockedUntil?: number;
  lastError?: string;
}

export type NewScheduledMessageRow = Omit<
  ScheduledMessageRow,
  "id" | "createdAt" | "status" | "attempts" | "txnId"
> & {
  id?: string;
  createdAt?: number;
  txnId?: string;
};

type ScheduledListener = () => void;

const listeners = new Set<ScheduledListener>();
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (channel) return channel;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = () => notifyScheduledMessagesChanged(false);
  return channel;
}

function notifyScheduledMessagesChanged(broadcast = true): void {
  for (const listener of listeners) listener();
  if (broadcast) getChannel()?.postMessage({ type: "changed" });
}

function closeChannel(): void {
  channel?.close();
  channel = null;
}

export function resetScheduledMessagesForTest(): void {
  listeners.clear();
  closeChannel();
}

export function subscribeScheduledMessages(listener: ScheduledListener): () => void {
  listeners.add(listener);
  getChannel();
  return () => {
    listeners.delete(listener);
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("userId", "userId", { unique: false });
        store.createIndex("sendAt", "sendAt", { unique: false });
      } else {
        const store = req.transaction?.objectStore(STORE_NAME);
        if (store?.indexNames.contains("orgId")) store.deleteIndex("orgId");
        if (store && !store.indexNames.contains("userId")) {
          store.createIndex("userId", "userId", { unique: false });
        }
        if (store && !store.indexNames.contains("sendAt")) {
          store.createIndex("sendAt", "sendAt", { unique: false });
        }
        if (store) migrateScheduledIds(store);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function migrateScheduledIds(store: IDBObjectStore): void {
  const req = store.openCursor();
  req.onsuccess = () => {
    const cursor = req.result;
    if (!cursor) return;
    const raw = cursor.value as Partial<ScheduledMessageRow>;
    if (typeof raw.id === "string" && raw.id.includes("::")) {
      cursor.continue();
      return;
    }
    if (typeof raw.userId !== "string" || raw.userId.length === 0 || typeof raw.id !== "string") {
      cursor.delete();
      cursor.continue();
      return;
    }
    store.put({ ...raw, id: `${raw.userId}::${raw.id}` });
    cursor.delete();
    cursor.continue();
  };
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  const db = await openDb();
  return await new Promise<T | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    let value: T | undefined;
    const request = run(store);
    if (request) {
      request.onsuccess = () => {
        value = request.result;
      };
    }
    tx.oncomplete = () => {
      db.close();
      resolve(value);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error ?? new Error("scheduled messages tx aborted"));
    };
  });
}

function normaliseRow(raw: unknown): ScheduledMessageRow | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Partial<ScheduledMessageRow>;
  if (typeof row.id !== "string" || row.id.length === 0) return null;
  if (typeof row.roomId !== "string" || row.roomId.length === 0) return null;
  if (typeof row.userId !== "string" || row.userId.length === 0) return null;
  if (typeof row.body !== "string" || row.body.trim().length === 0) return null;
  if (typeof row.sendAt !== "number" || !Number.isFinite(row.sendAt)) return null;
  if (typeof row.createdAt !== "number" || !Number.isFinite(row.createdAt)) return null;
  const status =
    row.status === "dispatching" || row.status === "failed" || row.status === "pending"
      ? row.status
      : "pending";
  return {
    id: row.id,
    roomId: row.roomId,
    sendAt: row.sendAt,
    body: row.body,
    formattedBody: typeof row.formattedBody === "string" ? row.formattedBody : undefined,
    userId: row.userId,
    txnId: typeof row.txnId === "string" && row.txnId.length > 0 ? row.txnId : row.id,
    createdAt: row.createdAt,
    status,
    attempts: typeof row.attempts === "number" && Number.isFinite(row.attempts) ? row.attempts : 0,
    nextAttemptAt:
      typeof row.nextAttemptAt === "number" && Number.isFinite(row.nextAttemptAt)
        ? row.nextAttemptAt
        : undefined,
    lockedUntil:
      typeof row.lockedUntil === "number" && Number.isFinite(row.lockedUntil)
        ? row.lockedUntil
        : undefined,
    lastError: typeof row.lastError === "string" ? row.lastError : undefined,
  };
}

export async function schedule(row: NewScheduledMessageRow): Promise<string> {
  const rawId = row.id ?? crypto.randomUUID();
  const id = rawId.includes("::") ? rawId : `${row.userId}::${rawId}`;
  const body = row.body.trim();
  if (body.length === 0) throw new Error("schedule: body must contain text");
  const next: ScheduledMessageRow = {
    id,
    roomId: row.roomId,
    sendAt: row.sendAt,
    body,
    formattedBody: row.formattedBody,
    userId: row.userId,
    txnId: row.txnId ?? id,
    createdAt: row.createdAt ?? Date.now(),
    status: "pending",
    attempts: 0,
  };
  await withStore("readwrite", (store) => {
    store.put(next);
  });
  notifyScheduledMessagesChanged();
  return id;
}

export async function wipeScheduledForUser(userId: string): Promise<void> {
  if (!userId) return;
  try {
    await withStore("readwrite", (store) => {
      const req = store.index("userId").openCursor(IDBKeyRange.only(userId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        cursor.delete();
        cursor.continue();
      };
    });
    notifyScheduledMessagesChanged();
    closeChannel();
  } catch {
    // Best-effort.
  }
}

export async function list(userId: string): Promise<ScheduledMessageRow[]> {
  try {
    const rows = await withStore<unknown[]>("readonly", (store) =>
      store.index("userId").getAll(IDBKeyRange.only(userId)),
    );
    return (rows ?? [])
      .map(normaliseRow)
      .filter((row): row is ScheduledMessageRow => row !== null)
      .sort((a, b) => a.sendAt - b.sendAt);
  } catch {
    return [];
  }
}

export async function cancel(id: string): Promise<void> {
  await withStore("readwrite", (store) => {
    store.delete(id);
  });
  notifyScheduledMessagesChanged();
}

export async function reschedule(id: string, sendAt: number): Promise<void> {
  await mutateRow(id, (row) => ({
    ...row,
    sendAt,
    status: "pending",
    attempts: 0,
    nextAttemptAt: undefined,
    lockedUntil: undefined,
    lastError: undefined,
  }));
}

export async function editBody(id: string, body: string): Promise<void> {
  const trimmed = body.trim();
  if (trimmed.length === 0) throw new Error("editBody: body must contain text");
  await mutateRow(id, (row) => ({ ...row, body: trimmed, formattedBody: undefined }));
}

export async function editBodyAndReschedule(
  id: string,
  body: string,
  sendAt: number,
): Promise<void> {
  const trimmed = body.trim();
  if (trimmed.length === 0) throw new Error("editBodyAndReschedule: body must contain text");
  if (!Number.isFinite(sendAt) || sendAt <= Date.now()) {
    throw new Error("editBodyAndReschedule: sendAt must be a future timestamp");
  }
  // Keep body and schedule in one transaction so users never see a half-saved edit.
  await mutateRow(id, (row) => ({
    ...row,
    body: trimmed,
    formattedBody: undefined,
    sendAt,
    status: "pending",
    attempts: 0,
    nextAttemptAt: undefined,
    lockedUntil: undefined,
    lastError: undefined,
  }));
}

export async function retryNow(id: string): Promise<void> {
  await mutateRow(id, (row) => ({
    ...row,
    status: "pending",
    attempts: 0,
    nextAttemptAt: Date.now(),
    lockedUntil: undefined,
    lastError: undefined,
  }));
}

async function mutateRow(
  id: string,
  update: (row: ScheduledMessageRow) => ScheduledMessageRow,
): Promise<void> {
  await withStore("readwrite", (store) => {
    const req = store.get(id);
    req.onsuccess = () => {
      const row = normaliseRow(req.result);
      if (!row) return;
      store.put(update(row));
    };
  });
  notifyScheduledMessagesChanged();
}

async function claimDueRows(now: number, userId: string): Promise<ScheduledMessageRow[]> {
  const db = await openDb();
  return await new Promise((resolve, reject) => {
    const claimed: ScheduledMessageRow[] = [];
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.index("userId").openCursor(IDBKeyRange.only(userId));
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      const row = normaliseRow(cursor.value);
      if (row) {
        const staleLock = row.status === "dispatching" && (row.lockedUntil ?? 0) <= now;
        const due = row.sendAt <= now && (row.nextAttemptAt ?? row.sendAt) <= now;
        if ((row.status === "pending" || staleLock) && due) {
          const next = {
            ...row,
            status: "dispatching" as const,
            lockedUntil: now + DISPATCH_LOCK_MS,
          };
          claimed.push(next);
          cursor.update(next);
        }
      }
      cursor.continue();
    };
    tx.oncomplete = () => {
      db.close();
      resolve(claimed);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error ?? new Error("scheduled claim tx aborted"));
    };
  });
}

function retryDelayMs(attempts: number): number {
  return Math.min(60_000, 5_000 * 2 ** Math.max(0, attempts - 1));
}

async function markSendResult(id: string, success: boolean, err?: unknown): Promise<void> {
  if (success) {
    await cancel(id);
    return;
  }
  await mutateRow(id, (row) => {
    const attempts = row.attempts + 1;
    const permanent = attempts >= MAX_ATTEMPTS;
    return {
      ...row,
      attempts,
      status: permanent ? "failed" : "pending",
      nextAttemptAt: permanent ? undefined : Date.now() + retryDelayMs(attempts),
      lockedUntil: undefined,
      lastError: err instanceof Error ? err.message : "Scheduled send failed",
    };
  });
}

export function startScheduledDispatcher(client: Client, userId: string): () => void {
  let stopped = false;
  let running = false;
  let nextTimer: ReturnType<typeof setTimeout> | null = null;

  const clearNextTimer = () => {
    if (nextTimer === null) return;
    clearTimeout(nextTimer);
    nextTimer = null;
  };

  const run = async () => {
    if (stopped || running) return;
    running = true;
    try {
      const due = await claimDueRows(Date.now(), userId);
      if (due.length > 0) notifyScheduledMessagesChanged();
      for (const row of due) {
        if (stopped) break;
        try {
          await sendScheduledText(client, row.roomId, row.body, row.formattedBody, row.txnId);
          await markSendResult(row.id, true);
        } catch (err) {
          await markSendResult(row.id, false, err);
        }
      }
    } finally {
      running = false;
    }
  };

  const reschedule = async () => {
    if (stopped || running) return;
    clearNextTimer();
    const rows = await list(userId);
    if (stopped || running) return;
    const nextDue = rows
      .filter((row) => row.status === "pending" || row.status === "dispatching")
      .map((row) => Math.max(row.sendAt, row.nextAttemptAt ?? row.sendAt, row.lockedUntil ?? 0));
    if (nextDue.length === 0) return;
    const delay = Math.min(MAX_TIMEOUT_MS, Math.max(0, Math.min(...nextDue) - Date.now()));
    nextTimer = setTimeout(() => {
      nextTimer = null;
      void run().finally(() => void reschedule());
    }, delay);
  };

  const onVisible = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      void reschedule();
    }
  };

  void run().finally(() => void reschedule());
  const unsubscribe = subscribeScheduledMessages(() => void reschedule());
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisible);
  }
  return () => {
    stopped = true;
    clearNextTimer();
    unsubscribe();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisible);
    }
  };
}

export const SCHEDULED_DISPATCH_MAX_TIMEOUT_MS = MAX_TIMEOUT_MS;
